import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resolveTenantTheme, type ResolvedTenantBranding } from '@trellis/shared';
import { api } from './api';

export interface TenantBrandingResponse {
  slug: string;
  name: string;
  locale: string;
  branding: Partial<ResolvedTenantBranding> | null;
}

interface BrandingContextValue {
  slug: string | null;
  /** La direccion actual de la institución. Distinta del slug si se renombro. */
  canonicalSlug: string | null;
  tenantName: string | null;
  programName: string;
  branding: Partial<ResolvedTenantBranding> | null;
  loading: boolean;
  notFound: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  slug: null,
  canonicalSlug: null,
  tenantName: null,
  programName: 'Trellis',
  branding: null,
  loading: false,
  notFound: false,
});

/**
 * Aplica la marca de la institución.
 *
 * Las variables CSS se escriben sobre `document.documentElement` y no sobre un
 * contenedor porque tienen que alcanzar también a lo que se renderiza en
 * portales — modales, menús desplegables, tooltips —, que viven fuera del árbol
 * de React de la página.
 */
export function BrandingProvider({
  slug,
  programSlug = null,
  children,
}: {
  slug: string | null;
  /**
   * Cuando se entra por el enlace de un programa, la marca es la del programa.
   *
   * Dos programas de la misma pharma tenían la misma puerta: la persona veía el
   * nombre de la institución y no el del acompañamiento en el que se anotó, que
   * es lo único que reconoce del material que le dieron.
   */
  programSlug?: string | null;
  children: ReactNode;
}) {
  const query = useQuery({
    queryKey: ['branding', slug, programSlug],
    queryFn: () =>
      api.get<TenantBrandingResponse>(
        programSlug
          ? `/public/tenants/${slug}/programs/${programSlug}/branding`
          : `/public/tenants/${slug}/branding`,
      ),
    enabled: Boolean(slug),
    // La marca de un programa no cambia entre pantallas: sin esto, cada
    // navegación dispararía una request y produciría un parpadeo de color.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const branding = query.data?.branding ?? null;

  useEffect(() => {
    const root = document.documentElement;
    const vars = resolveTenantTheme(branding);

    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }

    if (branding?.faviconUrl) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) link.href = branding.faviconUrl;
    }

    // Tipografía propia de la institución, si cargó una.
    let fontLink: HTMLLinkElement | null = null;
    if (branding?.fontUrl) {
      fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = branding.fontUrl;
      document.head.appendChild(fontLink);
    }

    let styleTag: HTMLStyleElement | null = null;
    if (branding?.customCss) {
      styleTag = document.createElement('style');
      styleTag.dataset.trellis = 'tenant-css';
      styleTag.textContent = branding.customCss;
      document.head.appendChild(styleTag);
    }

    // Al salir del programa hay que devolver el tema de Dx Tech: si no, pasar de
    // De una institución a /admin dejaría el backoffice pintado con su marca.
    return () => {
      for (const name of Object.keys(vars)) root.style.removeProperty(name);
      fontLink?.remove();
      styleTag?.remove();
    };
  }, [branding]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      slug,
      canonicalSlug: query.data?.slug ?? null,
      tenantName: query.data?.name ?? null,
      programName: branding?.programName ?? query.data?.name ?? 'Trellis',
      branding,
      loading: query.isLoading,
      notFound: query.isError,
    }),
    [slug, query.data, query.isLoading, query.isError, branding],
  );

  useEffect(() => {
    document.title = value.programName === 'Trellis' ? 'Trellis' : `${value.programName} · Trellis`;

    // Al salir de la institución el título vuelve al del producto. Sin esto, la
    // pestaña sigue anunciando el programa anterior en el backoffice de Dx
    // Tech o en la portada — y quien tiene varias abiertas se confunde de cuál
    // es cuál.
    return () => {
      document.title = 'Trellis';
    };
  }, [value.programName]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
